import { useState } from "react";
import { useSound, SOUNDS } from "@/context/SoundContext";
import { Play, Pause, SkipForward, SkipBack, X, ListMusic, Volume2, Music2 } from "lucide-react";

export const MiniPlayer = () => {
  const s = useSound();
  const [showList, setShowList] = useState(false);
  if (!s) return null;
  const { playing, currentSound, current, toggle, next, prev, volume, setVolume, playerOpen, setPlayerOpen, preview } = s;

  // Collapsed reopen pill — visible when the player is closed but audio is still active.
  if (!playerOpen) {
    if (!playing) return null;
    return (
      <button data-testid="miniplayer-reopen" onClick={() => setPlayerOpen(true)}
        className="fixed left-4 bottom-24 z-50 flex items-center gap-2 px-3 py-2 rounded-full border text-xs font-semibold text-white"
        style={{ borderColor: "var(--sfx-accent)", background: "rgba(6,12,24,0.95)", boxShadow: "0 0 20px var(--sfx-accent-glow)" }}>
        <Music2 className="h-4 w-4 animate-pulse" style={{ color: "var(--sfx-accent)" }} /> Now Playing
      </button>
    );
  }

  return (
    <>
      {/* Full track list overlay */}
      {showList && (
        <div className="fixed inset-0 z-[60] flex items-end" data-testid="miniplayer-list">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowList(false)} />
          <div className="relative w-full max-w-[480px] mx-auto bg-[#060c18] border-t border-[#14233d] rounded-t-3xl p-5 pb-8 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ListMusic className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
                <span className="font-heading font-semibold">Alert Sound Library</span>
              </div>
              <button data-testid="miniplayer-list-close" onClick={() => setShowList(false)} className="text-[#5f7ba6] hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-2">
              {SOUNDS.map((track, i) => {
                const on = i === current;
                return (
                  <button key={track.id} data-testid={`track-${track.id}`}
                    onClick={() => preview(i)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl border text-left transition-colors ${on ? "text-white" : "border-[#14233d] text-[#8fb4e6] hover:border-[#24406b]"}`}
                    style={on ? { borderColor: "var(--sfx-accent)", backgroundColor: "var(--sfx-accent-soft)" } : undefined}>
                    <div className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
                      {on && playing ? <Pause className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} /> : <Play className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />}
                    </div>
                    <span className="font-heading text-sm font-semibold flex-1">{track.name}</span>
                    {on && <span className="h-2 w-2 rounded-full animate-pulse" style={{ backgroundColor: "var(--sfx-accent)" }} />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bottom mini bar */}
      <div data-testid="miniplayer-bar"
        className="fixed left-1/2 -translate-x-1/2 bottom-[76px] z-50 w-[calc(100%-24px)] max-w-[456px] rounded-2xl border px-3 py-2.5 flex items-center gap-2"
        style={{ borderColor: "var(--sfx-accent)", background: "rgba(6,12,24,0.97)", boxShadow: "0 0 26px var(--sfx-accent-glow)" }}>
        <button onClick={() => setShowList(true)} data-testid="miniplayer-open-list" className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
          <Music2 className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-xs font-semibold text-white truncate">{currentSound?.name}</div>
          <div className="font-mono-term text-[9px] uppercase tracking-widest text-[#5f7ba6]">{playing ? "Playing" : "Paused"}</div>
        </div>
        <button onClick={prev} data-testid="miniplayer-prev" className="text-[#8fb4e6] hover:text-white p-1"><SkipBack className="h-4 w-4" /></button>
        <button onClick={toggle} data-testid="miniplayer-toggle" className="h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--sfx-accent)" }}>
          {playing ? <Pause className="h-4 w-4 text-white" fill="white" /> : <Play className="h-4 w-4 text-white" fill="white" />}
        </button>
        <button onClick={next} data-testid="miniplayer-next" className="text-[#8fb4e6] hover:text-white p-1"><SkipForward className="h-4 w-4" /></button>
        <div className="hidden sm:flex items-center gap-1.5 ml-1">
          <Volume2 className="h-4 w-4 text-[#5f7ba6]" />
          <input type="range" min="0" max="1" step="0.05" value={volume} data-testid="miniplayer-volume"
            onChange={(e) => setVolume(Number(e.target.value))} className="w-16 accent-[var(--sfx-accent)]" />
        </div>
        <button onClick={() => setPlayerOpen(false)} data-testid="miniplayer-close" className="text-[#5f7ba6] hover:text-white p-1 ml-0.5">
          <X className="h-4 w-4" />
        </button>
      </div>
    </>
  );
};
